import React, {
  useState,
  ReactNode,
  Children,
  isValidElement,
  useContext,
  useCallback,
} from 'react';
import styles from './accordion.module.scss';
import classNames from 'classnames';
import { ChevronDown } from '@carbon/react/icons';

interface AccordionProps {
  children: ReactNode;
  style?: React.CSSProperties;
  className?: string;
  /** Seed the initial open/closed state (uncontrolled). Defaults to false. */
  defaultExpanded?: boolean;
  /** Controlled expanded state. When provided, the component is fully controlled. */
  expanded?: boolean;
  /** Callback fired when the expanded state changes. */
  onClick?: (expanded: boolean) => void;
}

interface AccordionSummaryProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  expandIcon?: ReactNode;
  className?: string;
}

interface AccordionDetailsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
}

interface AccordionContextType {
  expanded: boolean;
  toggle: (e: React.MouseEvent) => void;
}

const AccordionContext = React.createContext<AccordionContextType | null>(null);

const useAccordion = () => useContext(AccordionContext) as AccordionContextType;

const getElementByComponentName = (elements: ReactNode[], name: string) =>
  elements.find(
    (element) =>
      isValidElement(element) &&
      typeof element.type === 'function' &&
      ((element.type as any).displayName || (element.type as any).name) === name
  );

export const Accordion: React.FC<AccordionProps> = ({
  children,
  className = '',
  defaultExpanded = false,
  expanded: controlledExpanded,
  onClick,
  ...props
}) => {
  const [internalExpanded, setInternalExpanded] =
    useState<boolean>(defaultExpanded);

  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const next = !expanded;
      if (!isControlled) {
        setInternalExpanded(next);
      }
      onClick?.(next);
    },
    [expanded, isControlled, onClick]
  );

  const elements = Children.toArray(children) as ReactNode[];
  const accordionSummary = getElementByComponentName(
    elements,
    'AccordionSummary'
  );
  const accordionDetails = getElementByComponentName(
    elements,
    'AccordionDetails'
  );

  return (
    <AccordionContext.Provider value={{ expanded, toggle: handleToggle }}>
      <div className={className} {...props}>
        {accordionSummary}
        {accordionDetails}
      </div>
    </AccordionContext.Provider>
  );
};

export const AccordionSummary: React.FC<AccordionSummaryProps> = ({
  children,
  expandIcon,
  className,
  ...props
}) => {
  const { toggle, expanded: isExpanded } = useAccordion();

  return (
    <div
      className={classNames(styles.accordionSummary, className)}
      onClick={toggle}
      {...props}
    >
      <div className={styles.accordionContent}>{children}</div>
      <span
        className={classNames(styles.expandIcon, {
          [styles.expanded]: isExpanded,
        })}
      >
        {expandIcon || <ChevronDown />}
      </span>
    </div>
  );
};

AccordionSummary.displayName = 'AccordionSummary';

export const AccordionDetails: React.FC<AccordionDetailsProps> = ({
  children,
  className,
  ...props
}) => {
  const { expanded: isExpanded } = useAccordion();

  return (
    <div
      className={classNames(
        styles.accordionDetails,
        {
          [styles.expanded]: isExpanded,
        },
        className
      )}
      aria-hidden={!isExpanded}
      {...props}
    >
      <div>{children}</div>
    </div>
  );
};

AccordionDetails.displayName = 'AccordionDetails';
